import { useEffect, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Loader2, Link2, Play, RefreshCw, ArrowLeft, Eye, Download } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix 2026-10-25 (test report it118, CRÍTICO): solo estos jobs saben simular de
// verdad (dry_run real en el backend) — el resto SIEMPRE ejecuta de verdad,
// pase lo que pase con el interruptor, y el backend ahora rechaza con 400 si
// se les manda dry_run=true por error (ver _reject_unsupported_dry_run).
const DRY_RUN_SUPPORTED = new Set([
  "run-all", "apply-director-overrides", "migrate-legacy-statuses", "fix-innohub-refs",
  "sync-iniciativas-create-missing", "migrate-pg07-adjunto-docs", "migrate-iso-docs",
  // Fix 2026-10-25 (ticket R-3, it119-18:13): estas 7 ya estiman ficheros/GB
  // leyendo metadatos de Airtable, sin descargar nada — antes rechazaban dry_run.
  "sync-iniciativas-attachments", "sync-ecosystem-attachments", "sync-contacts-attachments",
  "sync-programas-attachments", "sync-dealflow-attachments", "sync-historical-attachments",
  "archive-orphan-tables",
  // Fix 2026-10-25 (it119 19:41): job 5, puramente aditivo, ahora también simula.
  "sync-project-docs",
]);

// Fix 2026-10-25 (ticket R-6, it119-18:13): jobs en background (BackgroundTasks)
// devuelven {"status":"started"} al instante — el fetch termina en <1s aunque el
// job siga corriendo varios minutos en el servidor. Antes eso dejaba el botón
// "disabled" solo durante ese segundo, así que un admin confundido por la falta
// de aviso podía volver a pulsar y lanzar el job 2 veces. Ahora se marca "en
// curso" de verdad hasta que el Historial muestre una entrada más reciente que
// el momento del lanzamiento (se comprueba en cada "Refrescar").
const BACKGROUND_JOBS = new Set([
  "sync-iniciativas-attachments", "sync-ecosystem-attachments", "sync-contacts-attachments",
  "sync-programas-attachments", "sync-dealflow-attachments", "sync-historical-attachments",
  "migrate-pg07-adjunto-docs", "migrate-iso-docs", "archive-orphan-tables",
]);

const OPERATIONS = [
  { key: "dedupe-portfolio-pg07", statusKey: "dedupe_portfolio_pg07", label: "1 · Dedupe Portfolio ↔ PG-07", desc: "Vincula iniciativas con proyectos PG-07 por airtable_id (no por nombre)" },
  { key: "sync-pms", statusKey: "sync_pms_experts", label: "2 · Sync PMs y Expertos", desc: "Airtable PMs → users + partners" },
  { key: "sync-pilots", statusKey: "sync_pilots_from_pg07", label: "3 · PG-07 (Piloto) → Pilots", desc: "Proyectos en fase Piloto crean/actualizan pilots" },
  { key: "sync-partners", statusKey: "sync_partners_from_pg07", label: "4 · Sponsors/Partners PG-07 → Partners", desc: "Extrae sponsors y partners de cada proyecto" },
  { key: "sync-project-docs", statusKey: "sync_project_docs", label: "5 · Attachments PG-07 → Documentación", desc: "URLs de documentos de proyectos → rd_documents" },
  { key: "link-grant-calls", statusKey: "link_grant_calls", label: "6 · Convocatorias PG-07 → Grant calls", desc: "call_program/financing_org → grant_calls" },
  { key: "import-iso-docs", statusKey: "import_iso_docs", label: "7 · Import ISO 166002 base", desc: "Base Sistema Gestión I+D+i → documentación" },
  { key: "sync-seguimiento-anual", statusKey: "sync_seguimiento_anual", label: "8 · Seguimiento anual 2023-2026 → Iniciativas", desc: "Airtable Iniciativas (texto libre por año) → ficha." },
  { key: "sync-iniciativas-attachments", statusKey: "sync_iniciativas_attachments", label: "9 · Imagen + Documento → Object Storage", desc: "Adjuntos de Iniciativas migrados fuera de Airtable." },
  { key: "sync-dealflow", statusKey: "sync_dealflow", label: "10 · Dealflow (Amount/Stage) → Dealflow Deals", desc: "Tabla real Observatorio/Dealflow con importes y probabilidad de cierre" },
  { key: "sync-iniciativas-create-missing", statusKey: "sync_iniciativas_create_missing", label: "11 · Crear iniciativas huérfanas de Airtable", desc: "Iniciativas en la maestra que nunca se crearon en el OS (idempotente)" },
  { key: "sync-ecosystem-defaults", statusKey: "sync_ecosystem_defaults", label: "12 · Backfill Pipeline/Status Ecosistema", desc: "Agentes sin Pipeline/Status → valor inicial del embudo" },
  { key: "sync-contacts", statusKey: "sync_contacts", label: "13 · Sync Contactos", desc: "Airtable Observatorio/Contacts → Contactos" },
  { key: "sync-interactions", statusKey: "sync_interactions", label: "14 · Sync Interacciones", desc: "Airtable Observatorio/Interactions → Interacciones" },
  { key: "sync-climatetech-accelerators", statusKey: "sync_climatetech_accelerators", label: "15 · Sync ClimateTech Accelerators", desc: "Airtable Observatorio/Investors → ClimateTech Accelerators" },
  { key: "sync-ecosystem-attachments", statusKey: "sync_ecosystem_attachments", label: "16 · Logo/Files Ecosistema → Object Storage", desc: "692 agentes — tarda varios minutos, corre en background" },
  { key: "sync-contacts-attachments", statusKey: "sync_contacts_attachments", label: "17 · Picture Contactos → Object Storage", desc: "448 contactos — corre en background" },
  { key: "sync-programas-attachments", statusKey: "sync_programas_attachments", label: "18 · Imagen/Lecciones Programas → Object Storage", desc: "Corre en background" },
  { key: "sync-dealflow-attachments", statusKey: "sync_dealflow_attachments", label: "19 · Deal Docs Dealflow → Object Storage", desc: "Corre en background" },
  { key: "sync-historical-attachments", statusKey: "sync_historical_attachments", label: "20 · Adjuntos históricos 2023-2026 → Object Storage", desc: "8 tablas por año — tarda varios minutos, corre en background" },
  { key: "migrate-pg07-adjunto-docs", statusKey: "migrate_pg07_adjunto_docs", label: "21 · Re-migrar PG07_Adjunto (URLs caducadas)", desc: "Re-descarga de Airtable en vivo — corre en background" },
  { key: "migrate-iso-docs", statusKey: "migrate_iso_docs", label: "22 · Re-migrar documentos ISO 166002 (URLs caducadas)", desc: "Re-descarga de Airtable en vivo — corre en background" },
  { key: "archive-orphan-tables", statusKey: "archive_orphan_tables", label: "23 · Archivar Campaign/FeedBack/Fechas Clave", desc: "Bases sin entidad equivalente en el OS — corre en background" },
  { key: "apply-director-overrides", statusKey: "apply_director_overrides", label: "24 · Aplicar correcciones del Director (Estado)", desc: "KmH/CLIMA-vhs eléctricos/TECNOCIR→Completado, MATMAX/SIBIT-Korea→Cancelado (bloqueados)" },
  { key: "migrate-legacy-statuses", statusKey: "migrate_legacy_statuses", label: "24b · Migrar 'En Evaluación'/'En Validación' → Backlog", desc: "Esos 2 estados se retiran del catálogo (acordado 25/10/2026)" },
  { key: "fix-innohub-refs", statusKey: "fix_innohub_refs", label: "25 · Corregir carpeta InnoHub de CICLICOM + rellenar enlaces", desc: "CICLICOM estaba en 1.EN CURSO, la carpeta real está en 4.CERRADOS" },
  { key: "find-duplicate-candidates", statusKey: "find_duplicate_candidates", label: "26 · Detectar candidatos a duplicado", desc: "Revisar en /admin/duplicados — nunca fusiona solo" },
];

export default function CrossLinkAdminPage() {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [history, setHistory] = useState([]);
  const [dryRun, setDryRun] = useState(true);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [bgRunning, setBgRunning] = useState({}); // { [opKey]: ISOString de cuándo se lanzó }
  const [serverRunningJobs, setServerRunningJobs] = useState([]); // heartbeat del servidor (sobrevive a recargas/otros admins)
  const navigate = useNavigate();

  const loadStatus = async () => {
    try {
      const r = await fetch(`${API}/cross-link/status`, { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" });
      if (r.ok) {
        const j = await r.json();
        setStatus(j.stats);
        setLastRun(j.last_run);
        setHistory(j.history || []);
        setServerRunningJobs(j.running_jobs || []);
        // Si el Historial ya tiene una entrada más reciente que el lanzamiento,
        // el job de background terminó — se puede volver a habilitar el botón.
        // Fix 2026-10-25 (it119 testing): el toast se calcula FUERA del updater de
        // estado (antes estaba dentro del setBgRunning, y React StrictMode llama al
        // updater dos veces en dev, duplicando el toast "terminó").
        const justFinished = [];
        setBgRunning((prev) => {
          const next = { ...prev };
          for (const op of OPERATIONS) {
            const startedAt = prev[op.key];
            if (!startedAt) continue;
            const lastFinished = (j.last_run || {})[op.statusKey]?.finished_at;
            const finished = lastFinished && new Date(lastFinished) > new Date(startedAt);
            if (finished) {
              delete next[op.key];
              justFinished.push(op.label);
            }
          }
          return next;
        });
        justFinished.forEach((label) => toast.success(`${label} terminó — revisa el resultado en el Historial`));
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, []);

  const runOp = async (opKey, opLabel) => {
    const simulate = dryRun && DRY_RUN_SUPPORTED.has(opKey);
    setRunning(opKey);
    setDryRunResult(null);
    try {
      const r = await fetch(`${API}/cross-link/${opKey}${simulate ? "?dry_run=true" : ""}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { toast.error(`Error ${opKey}: ${r.status}`); return; }
      const j = await r.json();
      if (simulate) {
        setDryRunResult({ op: opKey, result: j });
        toast.info(`Simulación ${opKey}: nada escrito — revisa el resultado abajo`);
      } else if (j.status === "started" && BACKGROUND_JOBS.has(opKey)) {
        // Fix R-6: aviso inmediato + bloqueo persistente del botón mientras corre.
        setBgRunning((prev) => ({ ...prev, [opKey]: new Date().toISOString() }));
        toast.info(`${opLabel}: job lanzado — corriendo en segundo plano, puede tardar varios minutos. Pulsa "Refrescar" para ver cuándo termina.`, { duration: 6000 });
      } else {
        toast.success(`${opKey}: ${JSON.stringify(j).slice(0, 120)}`);
        loadStatus();
      }
    } finally { setRunning(null); }
  };

  const runAll = async () => {
    // Fix 2026-10-25 (ticket R-6): antes de ejecutar TODO de verdad, se pide
    // confirmación explícita listando los jobs que NO tienen simulación y van
    // a escribir sin poder previsualizarse (26 jobs en total, solo ~7 simulan).
    if (!dryRun) {
      setRunning("all");
      let previewFailed = false;
      try {
        const preview = await fetch(`${API}/cross-link/run-all?dry_run=true`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
        if (preview.ok) {
          const pj = await preview.json();
          const realJobs = pj.would_execute_for_real || [];
          if (realJobs.length > 0) {
            const ok = window.confirm(
              `${realJobs.length} de los jobs NO tienen modo simulación y van a ejecutarse de verdad sin previsualización:\n\n${realJobs.join(", ")}\n\n¿Continuar con "Ejecutar TODO"?`
            );
            if (!ok) { setRunning(null); return; }
          }
        } else {
          previewFailed = true;
        }
      } catch {
        previewFailed = true;
      }
      // Fix 2026-10-25 (it119 testing, RCA R-6): si la vista previa falla (red/HTTP),
      // antes se seguía directo a ejecutar TODO de verdad sin ningún aviso. Ahora se
      // exige una confirmación genérica explícita en ese caso, nunca se omite.
      if (previewFailed) {
        const ok = window.confirm(
          "No se pudo obtener la vista previa de qué jobs escribirían de verdad (fallo de red o del servidor).\n\n¿Aun así quieres continuar y ejecutar TODO de verdad?"
        );
        if (!ok) { setRunning(null); return; }
      }
    }
    setRunning("all");
    setDryRunResult(null);
    try {
      const r = await fetch(`${API}/cross-link/run-all${dryRun ? "?dry_run=true" : ""}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { toast.error(`Error: ${r.status}`); return; }
      const j = await r.json();
      if (dryRun) {
        setDryRunResult({ op: "run-all", result: j });
        toast.info("Simulación completa: nada escrito — revisa el resultado abajo");
      } else {
        setLastRun(j);
        toast.success(`Cross-link completo (${Object.keys(j).length} operaciones)`);
        loadStatus();
      }
    } finally { setRunning(null); }
  };

  const downloadReconciliationCsv = async () => {
    try {
      const r = await fetch(`${API}/cross-link/attachment-migration-report.csv`, { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" });
      if (!r.ok) { toast.error(`Error ${r.status}`); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `reconciliacion-adjuntos-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo exportar el CSV");
    }
  };

  return (
    <div className="space-y-6" data-testid="cross-link-admin-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <button className="text-xs text-emerald-700 hover:underline flex items-center gap-1 mb-1" onClick={() => navigate("/admin")} data-testid="back-to-admin">
            <ArrowLeft className="w-3 h-3" /> Volver a Administración
          </button>
          <div className="text-[10px] uppercase tracking-widest text-violet-700 font-semibold">Automatización · Airtable ↔ MongoDB</div>
          <h1 className="text-3xl font-black flex items-center gap-2"><Link2 className="w-7 h-7 text-violet-600" /> Cross-Link</h1>
          <p className="text-sm text-muted-foreground mt-1">Vincula datos entre módulos automáticamente. Se ejecuta también en cron diario a las 06:00 UTC.</p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-2 w-full sm:w-auto">
          <div className="flex items-center gap-2 text-xs flex-wrap" data-testid="cl-dry-run-toggle">
            <Switch checked={dryRun} onCheckedChange={setDryRun} id="dry-run-switch" />
            <label htmlFor="dry-run-switch" className="cursor-pointer select-none font-medium">
              Modo simulación {dryRun ? <span className="text-amber-600">(activado — nada se escribe)</span> : <span className="text-red-600">(desactivado — escribe de verdad)</span>}
            </label>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={downloadReconciliationCsv} data-testid="cl-export-reconciliation-csv">
              <Download className="w-3.5 h-3.5 mr-1" />CSV reconciliación
            </Button>
            <Button variant="outline" size="sm" onClick={loadStatus} data-testid="cl-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1" />Refrescar
            </Button>
            <Button onClick={runAll} disabled={running !== null} data-testid="cl-run-all">
              {running === "all" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : dryRun ? <Eye className="w-3.5 h-3.5 mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />}
              {dryRun ? "Simular TODO" : "Ejecutar TODO"}
            </Button>
          </div>
        </div>
      </div>

      {serverRunningJobs.length > 0 && (
        <div className="space-y-2" data-testid="cl-server-running-jobs">
          {serverRunningJobs.map((rj) => {
            const label = OPERATIONS.find((o) => o.statusKey === rj.step_name)?.label || rj.step_name;
            return (
              <div
                key={rj.step_name}
                data-testid={`cl-server-running-${rj.step_name}`}
                className={`text-xs rounded-md border px-3 py-2 flex items-center gap-2 ${rj.possibly_interrupted ? "border-red-400 bg-red-50 dark:bg-red-950/30 text-red-700" : "border-blue-400 bg-blue-50 dark:bg-blue-950/30 text-blue-700"}`}
              >
                <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                {rj.possibly_interrupted ? (
                  <span><strong>{label}</strong>: lleva {rj.minutes_running} min "en curso" — probablemente se interrumpió (reinicio/despliegue) y no llegó a registrar resultado. Puedes relanzarlo con seguridad: es idempotente.</span>
                ) : (
                  <span><strong>{label}</strong>: en curso desde hace {rj.minutes_running ?? "?"} min (dato del servidor, visible para cualquier admin).</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {dryRunResult && (
        <Card className="border-amber-400" data-testid="cl-dry-run-result">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold mb-2 text-amber-700">Resultado de la simulación — {dryRunResult.op} (nada se ha escrito)</h3>
            <pre className="text-[10px] bg-amber-50 dark:bg-amber-950/30 p-3 rounded overflow-auto max-h-80">
{JSON.stringify(dryRunResult.result, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      {loading ? <div className="flex justify-center p-10"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div> : (
        <>
          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-bold mb-3">Estado de vinculación por colección</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cl-stats">
                {status && Object.entries(status).map(([coll, s]) => (
                  <div key={coll} className="border rounded p-3" data-testid={`cl-stat-${coll}`}>
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{coll}</div>
                    <div className="text-2xl font-black">{s.linked} / {s.total}</div>
                    <div className="mt-1 h-1.5 bg-slate-200 rounded overflow-hidden">
                      <div className={`h-full ${s.pct >= 80 ? "bg-emerald-500" : s.pct >= 40 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${s.pct}%` }} />
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">{s.pct}% vinculados</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="space-y-2">
            {OPERATIONS.map(op => (
              <Card key={op.key} data-testid={`cl-op-${op.key}`}>
                <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[280px]">
                    <div className="font-semibold text-sm">{op.label}</div>
                    <div className="text-xs text-muted-foreground">{op.desc}</div>
                  </div>
                  {lastRun && lastRun[op.statusKey] && (
                    <Badge variant="outline" className="text-[10px]" data-testid={`cl-last-${op.key}`}>
                      {lastRun[op.statusKey].finished_at
                        ? `Último: ${new Date(lastRun[op.statusKey].finished_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })} · `
                        : "Último: "}
                      {JSON.stringify(lastRun[op.statusKey]).slice(0, 60)}
                    </Badge>
                  )}
                  {dryRun && !DRY_RUN_SUPPORTED.has(op.key) && (
                    <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-400" title="Esta operación no tiene modo simulación — el botón siempre ejecuta de verdad">
                      sin simulación — ejecuta de verdad
                    </Badge>
                  )}
                  {bgRunning[op.key] && (
                    <Badge variant="outline" className="text-[10px] text-blue-700 border-blue-400 animate-pulse" data-testid={`cl-bg-running-${op.key}`}>
                      En curso desde {new Date(bgRunning[op.key]).toLocaleTimeString("es-ES")} — pulsa "Refrescar" para comprobar si terminó
                    </Badge>
                  )}
                  <Button
                    variant="outline" size="sm" onClick={() => runOp(op.key, op.label)}
                    disabled={running !== null || !!bgRunning[op.key]}
                    data-testid={`cl-run-${op.key}`}
                  >
                    {running === op.key || bgRunning[op.key] ? (
                      <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    ) : dryRun && DRY_RUN_SUPPORTED.has(op.key) ? (
                      <Eye className="w-3 h-3 mr-1" />
                    ) : (
                      <Play className="w-3 h-3 mr-1" />
                    )}
                    {bgRunning[op.key] ? "En curso…" : dryRun && DRY_RUN_SUPPORTED.has(op.key) ? "Simular" : "Ejecutar"}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {history.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-bold mb-2">Historial (últimos {history.length} jobs)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="cl-history-table">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b">
                        <th className="py-1 pr-3">Job</th>
                        <th className="py-1 pr-3">Finalizado</th>
                        <th className="py-1">Resultado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h, i) => (
                        <tr key={i} className="border-b last:border-0" data-testid={`cl-history-row-${i}`}>
                          <td className="py-1.5 pr-3 font-medium">{OPERATIONS.find(o => o.statusKey === h.step_name)?.label || h.step_name}</td>
                          <td className="py-1.5 pr-3 text-muted-foreground">{h.finished_at ? new Date(h.finished_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                          <td className="py-1.5 font-mono text-[10px]">{JSON.stringify(h.result).slice(0, 80)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {lastRun && (
            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-bold mb-2">Última ejecución completa</h3>
                <pre className="text-[10px] bg-slate-50 dark:bg-slate-900 p-3 rounded overflow-auto max-h-80" data-testid="cl-last-run">
{JSON.stringify(lastRun, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
